export const userStatusNames = {
  ACTIVE: 'active',
  DISABLED: 'disabled',
  INVITED: 'invited',
  UNVERIFIED: 'emailUnverified',
};

/**
 * A custom filter for status of a user.
 * @param {string} value The input value.
 * @param {object} record The filter object.
 */
export const filterStatus = (value, record) => {
  if (value === userStatusNames.ACTIVE) {
    return record.active && record.emailVerified;
  }
  if (value === userStatusNames.INVITED) {
    return !record.active && !record.emailVerified;
  }
  if (value === userStatusNames.DISABLED) {
    return !record.active && record.emailVerified;
  }
  if (value === userStatusNames.UNVERIFIED) {
    return record.active && !record.emailVerified;
  }
  return true;
};

/**
 * Gets the status of teh given user
 */
export const getStatus = (user) => {
  let color = 'green';
  let status = userStatusNames.ACTIVE;
  if (!user.emailVerified) {
    color = 'orange';
    if (user.active) {
      status = userStatusNames.UNVERIFIED;
    } else {
      status = userStatusNames.INVITED;
    }
  } else if (!user.active) {
    color = 'red';
    status = userStatusNames.DISABLED;
  }
  return { color, status };
};

/**
 * Since there's no way to get these from the server, I have added an array of options here for convenience.
 */
export const userStatuses = [
  { status: userStatusNames.ACTIVE, statusTranslationId: 'active' },
  { status: userStatusNames.DISABLED, statusTranslationId: 'inactive' },
  { status: userStatusNames.INVITED, statusTranslationId: 'invited' },
  { status: userStatusNames.UNVERIFIED, statusTranslationId: 'emailUnverified' },
];

export const roleNames = {
  INSPECTOR: 'inspector',
  ADMIN: 'group_administrator',
  READ_ONLY: 'read_only',
  CONSOLE_ADMIN: 'console_admin',
  CONSOLE_FINANCE: 'console_finance',
  COMPANY_ADMIN: 'company_admin',
  COMPANY_FINANCE: 'company_finance',
};

/**
 * Since there's no way to get these from the server, I have added an array of options here for convenience - CM.
 */
export const userRoles = [{ roleName: roleNames.INSPECTOR, roleTranslationId: 'inspector' }, { roleName: roleNames.ADMIN, roleTranslationId: 'administrator' },
                          { roleName: roleNames.CONSOLE_ADMIN, roleTranslationId: 'systemAdministrator' }, { roleName: roleNames.CONSOLE_FINANCE, roleTranslationId: 'financeAdministrator' },
                          { roleName: roleNames.COMPANY_ADMIN, roleTranslationId: 'companyAdministrator' }, { roleName: roleNames.COMPANY_FINANCE, roleTranslationId: 'companyFinance' }];

export const userIsAdmin = (user) => {
  if (!user || !user.linkedRoles) return false;
  return !!user.linkedRoles.find(roleName => roleName === roleNames.ADMIN);
};

export const userIsInspector = (user) => {
  if (!user || !user.linkedRoles) return false;
  return !!user.linkedRoles.find(roleName => roleName === roleNames.INSPECTOR);
};

// User has the system administrator (console admin) role (not be confused with userIsAdminConsole() on security-util.js)
export const userIsConsoleAdmin = (user) => {
  if (!user || !user.linkedRoles) return false;
  return !!user.linkedRoles.find(roleName => roleName === roleNames.CONSOLE_ADMIN);
};

export const userIsConsoleFinance = (user) => {
  if (!user || !user.linkedRoles) return false;
  return !!user.linkedRoles.find(roleName => roleName === roleNames.CONSOLE_FINANCE);
};

export const userIsCompanyAdmin = (user) => {
  if (!user || !user.linkedRoles) return false;
  return !!user.linkedRoles.find(roleName => roleName === roleNames.COMPANY_ADMIN);
};

export const userIsCompanyFinance = (user) => {
  if (!user || !user.linkedRoles) return false;
  return !!user.linkedRoles.find(roleName => roleName === roleNames.COMPANY_FINANCE);
};

export const userHasCompanyRolesButNotAdmin = (user) => {
  if (!user || !user.linkedRoles) return false;
  return !!user.linkedRoles.find(roleName => roleName === roleNames.COMPANY_ADMIN || roleName === roleNames.COMPANY_FINANCE)
      && !user.linkedRoles.find(roleName => roleName === roleNames.COMPANY_ADMIN);
};

export const userHasRoleName = (user, roleName) => {
  if (!user || !user.linkedRoles) return false;
  return !!user.linkedRoles.find(name => name === roleName);
};

/**
 * Method that takes a RESTAddress object and displays it in html (<br> between each item)
 */
export const displayAddressHTML = (address) => {
  if (!address) return '';

  let string = `${address.line1}<br>`;
  string = address.line2 ? `${string}${address.line2}<br>` : string;
  string = address.line3 ? `${string}${address.line3}<br>` : string;
  string = address.line4 ? `${string}${address.line4}<br>` : string;
  string = address.region ? `${string}${address.region}<br>` : string;
  string = address.zipPostcode ? `${string}${address.zipPostcode}<br>` : string;
  string = address.countryDescription ? `${string}${address.countryDescription}` : string;
  return string;
};

export const displayAddressSingleLine = (address) => {
  if (!address) return '';

  let string = `${address.line1}, `;
  string = address.line2 ? `${string}${address.line2}, ` : string;
  string = address.line3 ? `${string}${address.line3}, ` : string;
  string = address.line4 ? `${string}${address.line4}, ` : string;
  string = address.region ? `${string}${address.region}, ` : string;
  string = address.zipPostcode ? `${string}${address.zipPostcode}, ` : string;
  string = address.countryDescription ? `${string}${address.countryDescription}` : string;
  return string;
};

export const displayAddressMultiLine = (address) => {
  if (!address) return '';

  let string = `${address.line1}\n`;
  string = address.line2 ? `${string}${address.line2}\n` : string;
  string = address.line3 ? `${string}${address.line3}\n` : string;
  string = address.line4 ? `${string}${address.line4}\n` : string;
  string = address.region ? `${string}${address.region}\n` : string;
  string = address.zipPostcode ? `${string}${address.zipPostcode}\n` : string;
  string = address.countryDescription ? `${string}${address.countryDescription}` : string;
  return string;
};


/**
 * Converts all email substitution parameters to the passed in parameters
 * Defaults any missed parameters to an empty string
 * Feel free to a add to this as more parameters are created
 * @param email The email to substitute your given parameters into
 * @param parameters an object containing the parameters to replace ({ subParam: 'val' } will replace {sub-param} with 'val')
 */
export const substituteEmailParams = (
  email,
  {
    title, companyName, companyAddress, customerName, locationEmail, poweredByLogo, poweredByAlt, logoUrl, logo, contentNotMoblyx, emailUnwanted,
  },
) => {
  if (!email) return null;
  let newEmail = email.replace(/{title}/gi, title || '');
  newEmail = newEmail.replace(/{company-name}/gi, companyName || '');
  newEmail = newEmail.replace(/{company-address}/gi, companyAddress || '');
  newEmail = newEmail.replace(/{Customer Name}/gi, customerName || '');
  newEmail = newEmail.replace(/{Location Email}/gi, locationEmail || '');
  newEmail = newEmail.replace(/{powered-by-logo}/gi, poweredByLogo || '');
  newEmail = newEmail.replace(/{powered-by-alt}/gi, poweredByAlt || '');
  newEmail = newEmail.replace(/{logo}/gi, logo || '');
  newEmail = newEmail.replace(/{logo-data}/gi, logoUrl || '');
  newEmail = newEmail.replace(/{content-not-moblyx}/gi, contentNotMoblyx || '');
  newEmail = newEmail.replace(/{emailUnwanted}/gi, emailUnwanted || '');
  return newEmail;
};
